import React, { ChangeEvent, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as LeftArrowIcon } from '../../assets/images/icons/mevo/left-arrow.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/images/icons/right-arrow.svg';
import { useIsMobile } from '../../helpers/useIsMobile';
import { validateBirthDate, validateEmail, validatePhone } from '../../helpers/validations';
import FreeSampleHeader from './FreeSampleHeader';
import { Button } from '../UI-CHAKRA/Button';
import { Input } from '../UI-CHAKRA/Input/Input';
import { MaskedInput, MASKS } from '../UI-CHAKRA/Input/MaskedInput';
import { FormControl } from '../UI-CHAKRA/FormControl';
import { FormLabel } from '../UI-CHAKRA/FormLabel';
import { FormErrorMessage } from '../UI-CHAKRA/FormErrorMessage';
import { FreeSamplePrescriberForm } from '../../interfaces/freeSample';
import { isValidSpecialty } from '../../helpers/rqeSpecialties';
import FreeSampleSpecialtyCombobox from './FreeSampleSpecialtyCombobox';

interface FreeSamplePrescriberStepProps {
  laboratoryName: string;
  productName: string;
  form: FreeSamplePrescriberForm;
  onFormChange: (form: FreeSamplePrescriberForm) => void;
  // Fallback de RQE ainda em andamento — a especialidade pré-preenchida ainda não chegou.
  isResolvingSpecialty: boolean;
  onBack: () => void;
  onConfirm: () => void;
  onClose: () => void;
}

type Field = keyof FreeSamplePrescriberForm;

const ERRORS: Record<Field, string> = {
  email: 'Informe um e-mail válido',
  phone: 'Informe um telefone válido',
  birthdate: 'Informe uma data válida',
  specialty: 'Selecione sua especialidade',
};

export const isPrescriberFieldValid = (field: Field, form: FreeSamplePrescriberForm): boolean => {
  switch (field) {
    case 'email':
      return validateEmail(form.email);
    case 'phone':
      return validatePhone(form.phone);
    case 'birthdate':
      return validateBirthDate(form.birthdate);
    case 'specialty':
      return isValidSpecialty(form.specialty);
  }
};

const FreeSamplePrescriberStep: React.FC<FreeSamplePrescriberStepProps> = ({
  laboratoryName,
  productName,
  form,
  onFormChange,
  isResolvingSpecialty,
  onBack,
  onConfirm,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({});

  const updateForm = (patch: Partial<FreeSamplePrescriberForm>) => {
    onFormChange({ ...form, ...patch });
  };

  const markTouched = (field: Field) => () => setTouched((prev) => ({ ...prev, [field]: true }));
  const isInvalid = (field: Field) => !!touched[field] && !isPrescriberFieldValid(field, form);

  const requiredFields: Field[] = ['email', 'phone', 'birthdate', 'specialty'];
  const isFormValid = !isResolvingSpecialty && requiredFields.every((field) => isPrescriberFieldValid(field, form));

  return (
    <Flex flexDirection={'column'} height={'100%'} overflow="hidden">
      {isMobile ? (
        <Box flexShrink={0} borderBottom={'1px solid #E9ECEF'}>
          <Flex flexDirection={'row'} alignItems={'center'} gap={'8px'} padding={'20px'}>
            <LeftArrowIcon onClick={onBack} cursor={'pointer'} />
            <Text fontSize={'14px'} fontWeight={700} color={'primary.main'}>
              Voltar
            </Text>
          </Flex>
        </Box>
      ) : (
        <FreeSampleHeader onClose={onClose} />
      )}

      <Box flexGrow={1} overflowY={'auto'} overflowX="hidden" padding={'16px 20px'}>
        <Text fontSize={'12px'} fontWeight={700} color={'#6C757D'}>
          PRODUTO
        </Text>
        <Text fontSize={'16px'} fontWeight={700} color={'#343A40'} mb={'16px'}>
          {laboratoryName} - {productName}
        </Text>

        <Text fontSize={'16px'} fontWeight={700} color={'#343A40'}>
          Confirme seus dados
        </Text>
        <Text fontSize={'14px'} color={'#6C757D'} mb={'16px'}>
          O laboratório usa esses dados para identificar você e enviar a amostra:
        </Text>

        <Flex flexDirection={'column'} gap={'16px'}>
          <FormControl isInvalid={isInvalid('email')}>
            <FormLabel>E-mail*</FormLabel>
            <Input
              value={form.email}
              onChange={(e) => updateForm({ email: e.target.value })}
              onBlur={markTouched('email')}
              placeholder={'E-mail'}
              type={'email'}
            />
            <FormErrorMessage>{ERRORS.email}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={isInvalid('phone')}>
            <FormLabel>Telefone*</FormLabel>
            <MaskedInput
              mask={MASKS.BRAZIL_PHONE}
              value={form.phone}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateForm({ phone: e.target.value })}
              onBlur={markTouched('phone')}
              placeholder={'(11) 9 8765-4321'}
            />
            <FormErrorMessage>{ERRORS.phone}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={isInvalid('birthdate')}>
            <FormLabel>Data de nascimento*</FormLabel>
            <MaskedInput
              mask={MASKS.BIRTH_DATE}
              maskChar={''}
              value={form.birthdate}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateForm({ birthdate: e.target.value })}
              onBlur={markTouched('birthdate')}
              placeholder={'dd/mm/aaaa'}
            />
            <FormErrorMessage>{ERRORS.birthdate}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!isResolvingSpecialty && isInvalid('specialty')}>
            <FreeSampleSpecialtyCombobox
              label={'Especialidade*'}
              value={form.specialty}
              onChange={(specialty) => updateForm({ specialty })}
              onBlur={markTouched('specialty')}
              isLoading={isResolvingSpecialty}
            />
            <FormErrorMessage>{ERRORS.specialty}</FormErrorMessage>
          </FormControl>
        </Flex>
      </Box>

      <Box flexShrink={0} padding={'20px'} borderTop={'1px solid #E9ECEF'}>
        <Flex flexDirection={'row'} gap={'12px'} justifyContent={'flex-end'}>
          <Button
            onClick={onBack}
            padding="10px 20px"
            backgroundColor={'#FFF'}
            borderWidth={'2px'}
            borderColor={'primary.main'}
            borderStyle={'solid'}
            borderRadius={'20px'}
            _hover={{ backgroundColor: '#FFF', borderColor: '#6B409C' }}
          >
            <Text fontSize={'14px'} color={'primary.main'}>
              Voltar
            </Text>
          </Button>
          <Button
            onClick={onConfirm}
            isDisabled={!isFormValid}
            padding="10px 20px"
            backgroundColor={'primary.main'}
            borderRadius={'20px'}
            gap={'8px'}
            color={'#FFF'}
            fontSize={'14px'}
            _hover={{ backgroundColor: '#6B409C' }}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            rightIcon={<RightArrowIcon width={'16px'} height={'16px'} fill="#FFF" />}
          >
            Continuar
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

export default FreeSamplePrescriberStep;
